import { Component, computed, linkedSignal, output, input } from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { PAGINATION_DEFAULT_TOTAL_ITEMS } from '@shared/utils';

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-pagination',
  imports: [FormField, TranslatePipe],
  styleUrls: ['./pagination.component.scss'],
  template: `
    <div class="pagination-container">
      <div class="page-size-selector">
        <label for="pageSizeSelect">{{ 'PAGINATION.ITEMS_PER_PAGE' | translate }}:</label>
        <select
          id="pageSizeSelect"
          [formField]="pageSizeForm.pageSize"
          (change)="onPageSizeChange(+pageSizeForm.pageSize().value())">
          @for (size of pageSizeOptions; track size) {
            <option [value]="size">{{ size }}</option>
          }
        </select>
      </div>

      <ul class="pagination">
        <li [class.disabled]="page() === 1">
          <button type="button" (click)="goToPage(1)" [disabled]="page() === 1" aria-label="First Page">«</button>
        </li>

        <li [class.disabled]="page() === 1">
          <button type="button" (click)="goToPage(page() - 1)" [disabled]="page() === 1" aria-label="Previous Page">‹</button>
        </li>

        @for (pageItem of pages(); track trackByFn($index, pageItem)) {
          <li [class.active]="pageItem === page()" [class.disabled]="pageItem === '…'">
            @if (pageItem !== '…') {
              <button type="button" (click)="goToPage(pageItem)" [attr.aria-current]="pageItem === page() ? 'page' : null">
                {{ pageItem }}
              </button>
            } @else {
              <span class="ellipsis">…</span>
            }
          </li>
        }

        <li [class.disabled]="page() === totalPages()">
          <button type="button" (click)="goToPage(page() + 1)" [disabled]="page() === totalPages()" aria-label="Next Page">
            ›
          </button>
        </li>

        <li [class.disabled]="page() === totalPages()">
          <button type="button" (click)="goToPage(totalPages())" [disabled]="page() === totalPages()" aria-label="Last Page">
            »
          </button>
        </li>
      </ul>

      <div class="page-info">
        {{ 'PAGINATION.PAGE_INFO' | translate: { current: page(), total: totalPages() } }}
      </div>
    </div>
  `,
})
export class PaginationComponent {
  readonly page = input(1);
  readonly pageSize = input(10);
  readonly formValues = linkedSignal(() => ({ pageSize: String(this.pageSize()) }));
  readonly pageSizeForm = form(this.formValues);
  readonly totalItems = input(50);
  readonly maxPagesToShow = input(PAGINATION_DEFAULT_TOTAL_ITEMS);

  readonly pageChange = output<number>();
  readonly pageSizeChange = output<number>();

  pageSizeOptions = [5, 10, 20, 50];

  readonly totalPages = computed((): number => {
    const pageSize = this.pageSize() ?? 1;
    return Math.max(1, Math.ceil(this.totalItems() / Math.max(pageSize, 1)));
  });

  readonly pages = computed((): (number | string)[] => {
    const total = this.totalPages();
    const current = this.page();
    const max = this.maxPagesToShow();

    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const half = Math.floor(max / 2);

    let start = Math.max(2, current - half);
    let end = Math.min(total - 1, current + half);

    if (current <= half) {
      start = 2;
      end = max - 1;
    } else if (current + half >= total) {
      start = total - (max - 2);
      end = total - 1;
    }

    pages.push(1);

    if (start > 2) {
      pages.push('…');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push('…');
    }

    pages.push(total);

    return pages;
  });

  goToPage(page: number | string): void {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages() && page !== this.page()) {
      this.pageChange.emit(page);
    }
  }

  onPageSizeChange(size: number): void {
    if (size !== this.pageSize()) {
      this.pageSizeChange.emit(Number(size));
    }
  }

  trackByFn(index: number, page: number | string): string {
    return `${page}-${index}`;
  }
}
